import { trackAnalyticsSafely } from "./AnalyticsTracker";
import {
    NovedadesExportConfigurationTabID,
    NovedadesExportCreatePayload,
    NovedadesExportProcessPayload,
    NovedadesExportRestartOrigin,
    NovedadesExportStatusMappingPayload,
    NovedadesExportTabID,
    NovedadesExportTarget,
} from "./NovedadesExportEvents";

export class NovedadesExportAnalyticsHelper {
    static trackTabShown(processCount?: number) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.TabShown",
            payload: { processCount },
        });
    }

    static trackListRefreshed(processCount?: number) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.ListRefreshed",
            payload: { processCount },
        });
    }

    static trackProcessOpened(payload: NovedadesExportProcessPayload) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.ProcessOpened",
            payload,
        });
    }

    static trackCreateOpened() {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.CreateOpened",
        });
    }

    static trackCreateSubmitted(payload: NovedadesExportCreatePayload) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.CreateSubmitted",
            payload,
        });
    }

    static trackCreateSucceeded(payload: NovedadesExportCreatePayload & { processID: string }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.CreateSucceeded",
            payload,
        });
    }

    static trackCreateFailed(payload: NovedadesExportCreatePayload & { errorMessage?: string }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.CreateFailed",
            payload,
        });
    }

    static trackDetailTabSelected(payload: {
        processID: string;
        tabID: NovedadesExportTabID;
        status: string;
        currentStep?: string;
    }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.DetailTabSelected",
            payload,
        });
    }

    static trackProcessCompletedPolling(payload: NovedadesExportProcessPayload) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.ProcessCompletedPolling",
            payload,
        });
    }

    static trackProcessingRestartSubmitted(payload: {
        processID: string;
        status: string;
        currentStep?: string;
        origin: NovedadesExportRestartOrigin;
    }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.ProcessingRestartSubmitted",
            payload,
        });
    }

    static trackProcessingRestartSucceeded(payload: {
        processID: string;
        status: string;
        currentStep?: string;
        origin: NovedadesExportRestartOrigin;
    }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.ProcessingRestartSucceeded",
            payload,
        });
    }

    static trackProcessingRestartFailed(payload: {
        processID: string;
        status: string;
        currentStep?: string;
        origin: NovedadesExportRestartOrigin;
    }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.ProcessingRestartFailed",
            payload,
        });
    }

    static trackConfigurationOpened() {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.ConfigurationOpened",
        });
    }

    static trackConfigurationTabSelected(tabID: NovedadesExportConfigurationTabID) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.ConfigurationTabSelected",
            payload: { tabID },
        });
    }

    static trackStatusMappingColumnCreated(payload: {
        totalsField: string;
        customGroupCount: number;
        totalGroupCount: number;
    }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.StatusMappingColumnCreated",
            payload,
        });
    }

    static trackStatusMappingColumnRemoved(payload: {
        totalsField: string;
        customGroupCount: number;
        totalGroupCount: number;
    }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.StatusMappingColumnRemoved",
            payload,
        });
    }

    static trackStatusMappingNovedadAdded(payload: {
        totalsField: string;
        builtIn: boolean;
        novedadAcronym: string;
        groupAcronymCount: number;
        totalAssignedAcronymCount: number;
    }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.StatusMappingNovedadAdded",
            payload,
        });
    }

    static trackStatusMappingNovedadRemoved(payload: {
        totalsField: string;
        builtIn: boolean;
        novedadAcronym: string;
        groupAcronymCount: number;
        totalAssignedAcronymCount: number;
    }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.StatusMappingNovedadRemoved",
            payload,
        });
    }

    static trackStatusMappingSaveSubmitted(payload: NovedadesExportStatusMappingPayload) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.StatusMappingSaveSubmitted",
            payload,
        });
    }

    static trackStatusMappingSaveSucceeded(payload: NovedadesExportStatusMappingPayload) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.StatusMappingSaveSucceeded",
            payload,
        });
    }

    static trackStatusMappingSaveFailed(payload: NovedadesExportStatusMappingPayload) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.StatusMappingSaveFailed",
            payload,
        });
    }

    static trackStatusMappingCanceled(payload: NovedadesExportStatusMappingPayload) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.StatusMappingCanceled",
            payload,
        });
    }

    static trackExtraHoursProfileSelected(profileID: string) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.ExtraHoursProfileSelected",
            payload: { profileID },
        });
    }

    static trackExtraHoursSaveSubmitted(payload: { profileID: string; previousProfileID?: string }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.ExtraHoursSaveSubmitted",
            payload,
        });
    }

    static trackExtraHoursSaveSucceeded(payload: { profileID: string; previousProfileID?: string }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.ExtraHoursSaveSucceeded",
            payload,
        });
    }

    static trackExtraHoursSaveFailed(payload: { profileID: string; previousProfileID?: string }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.ExtraHoursSaveFailed",
            payload,
        });
    }

    static trackExtraHoursCanceled(payload: { profileID: string; previousProfileID?: string }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.ExtraHoursCanceled",
            payload,
        });
    }

    static trackEmployeesShown(payload: {
        processID: string;
        employeeCount: number;
        statusColumnCount: number;
        customStatusColumnCount: number;
    }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.EmployeesShown",
            payload,
        });
    }

    static trackExportTargetSelected(payload: {
        processID: string;
        target: NovedadesExportTarget;
    }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.ExportTargetSelected",
            payload,
        });
    }

    static trackExcelExportSubmitted(payload: {
        processID: string;
        employeeCount?: number;
        statusColumnCount?: number;
        customStatusColumnCount?: number;
        includeWorkedHours?: boolean;
    }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.ExcelExportSubmitted",
            payload,
        });
    }

    static trackExcelExportSucceeded(payload: {
        processID: string;
        employeeCount?: number;
        statusColumnCount?: number;
        customStatusColumnCount?: number;
        includeWorkedHours?: boolean;
    }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.ExcelExportSucceeded",
            payload,
        });
    }

    static trackExcelExportFailed(payload: {
        processID: string;
        employeeCount?: number;
        statusColumnCount?: number;
        customStatusColumnCount?: number;
        includeWorkedHours?: boolean;
    }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.ExcelExportFailed",
            payload,
        });
    }

    static trackSystemProfileSelected(payload: { processID: string; profileID: string }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.SystemProfileSelected",
            payload,
        });
    }

    static trackColppyTipoSelected(payload: { processID: string; tipo: string }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.ColppyTipoSelected",
            payload,
        });
    }

    static trackColppyConfigurationSaved(payload: { hasCode: boolean }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.ColppyConfigurationSaved",
            payload,
        });
    }

    static trackIncludeWorkedHoursToggled(payload: { processID: string; includeWorkedHours: boolean }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.IncludeWorkedHoursToggled",
            payload,
        });
    }

    static trackSystemFileGenerationSubmitted(payload: { processID: string; profileID: string; includeWorkedHours?: boolean }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.SystemFileGenerationSubmitted",
            payload,
        });
    }

    static trackSystemFileGenerationStarted(payload: { processID: string; profileID: string; includeWorkedHours?: boolean }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.SystemFileGenerationStarted",
            payload,
        });
    }

    static trackSystemFileGenerationFailed(payload: { processID: string; profileID: string; includeWorkedHours?: boolean }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.SystemFileGenerationFailed",
            payload,
        });
    }

    static trackSystemFileDownloaded(payload: {
        processID: string;
        profileID?: string;
        outputFilenamePresent: boolean;
    }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.SystemFileDownloaded",
            payload,
        });
    }

    static trackValidationErrorsShown(payload: {
        processID: string;
        errorsCount: number;
        lastErrorsCount: number;
    }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.ValidationErrorsShown",
            payload,
        });
    }

    static trackSystemFileRestartSubmitted(payload: { processID: string; profileID: string; includeWorkedHours?: boolean }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.SystemFileRestartSubmitted",
            payload,
        });
    }

    static trackSystemFileRestartStarted(payload: { processID: string; profileID: string; includeWorkedHours?: boolean }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.SystemFileRestartStarted",
            payload,
        });
    }

    static trackSystemFileRestartFailed(payload: { processID: string; profileID: string; includeWorkedHours?: boolean }) {
        trackAnalyticsSafely({
            eventType: "WebDashboard.NovedadesExport.SystemFileRestartFailed",
            payload,
        });
    }
}
